<template>
  <div>
    <!-- List View -->
    <div v-if="viewMode === 'list'">
      <PageHeader
        title="Suspect Coding Rules"
        subtitle="Rules that identify patients likely missing HCC codes based on clinical evidence"
        :heading-level="2"
      >
        <template #actions>
          <div class="flex gap-3 ml-auto">
            <button
              @click="runSelectedRules"
              :disabled="selectedRuleIds.length === 0 || runningRules"
              class="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {{ runningRules ? 'Running...' : `Run ${selectedRuleIds.length} Rule${selectedRuleIds.length !== 1 ? 's' : ''}` }}
            </button>
            <button
              @click="createNew"
              class="btn-primary"
            >
              Create New Rule
            </button>
          </div>
        </template>
      </PageHeader>

      <div class="p-4">

        <!-- Run Results Banner -->
        <div v-if="runResult" class="mb-4 rounded border" :class="runResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
          <div class="flex items-center justify-between">
            <div>
              <div class="font-medium" :class="runResult.success ? 'text-green-800' : 'text-red-800'">
                {{ runResult.success ? 'Rules evaluated and written to Snowflake' : 'Error running rules' }}
              </div>
              <div v-if="runResult.success" class="text-sm text-green-700 mt-1">
                {{ runResult.rules_evaluated }} rules evaluated, {{ runResult.total_suspects.toLocaleString() }} total suspects found
              </div>
              <div v-if="runResult.error" class="text-sm text-red-700 mt-1">{{ runResult.error }}</div>
              <!-- Per-rule breakdown -->
              <div v-if="runResult.rule_summaries?.length" class="mt-2 text-sm text-gray-600">
                <div v-for="s in runResult.rule_summaries" :key="s.rule_id">
                  {{ s.rule_name }}: <span class="font-medium">{{ (s.suspect_count || 0).toLocaleString() }}</span> suspects
                  <span v-if="s.error" class="text-red-600 ml-2">Error: {{ s.error }}</span>
                </div>
              </div>
            </div>
            <button @click="runResult = null" class="text-gray-400 hover:text-gray-600">&times;</button>
          </div>
        </div>

        <!-- Filters -->
        <div class="flex gap-4 mb-4">
          <select
            v-model="filterConfidenceTier"
            class="px-3 py-2 border border-gray-300 rounded text-sm bg-white"
          >
            <option value="">All Confidence Tiers</option>
            <option value="very_high">Very High</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            v-model="filterActive"
            class="px-3 py-2 border border-gray-300 rounded text-sm bg-white"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          <input
            v-model="filterSearch"
            type="text"
            placeholder="Search rules..."
            class="px-3 py-2 border border-gray-300 rounded text-sm flex-1"
          />
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="py-8">
          <LoadingIndicator message="Loading rules..." />
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredRules.length === 0" class="text-center py-8 text-gray-500">
          <p v-if="rules.length === 0">No suspect rules defined yet.</p>
          <p v-else>No rules match your filters.</p>
        </div>

        <!-- Rules Table -->
        <div v-else class="bg-white rounded border border-gray-200 overflow-hidden">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2 text-center w-8">
                  <input
                    type="checkbox"
                    :checked="allFilteredSelected"
                    @change="toggleSelectAll"
                    class="rounded border-gray-300"
                  />
                </th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer select-none" @click="toggleSort('name')">
                  Rule Name <span v-if="sortCol === 'name'" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">ICD Codes</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer select-none" @click="toggleSort('confidence_tier')">
                  Confidence <span v-if="sortCol === 'confidence_tier'" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer select-none" @click="toggleSort('evidence_type')">
                  Evidence <span v-if="sortCol === 'evidence_type'" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-3 py-2 text-right font-medium text-gray-700 cursor-pointer select-none" @click="toggleSort('conversion_rate')">
                  Conversion <span v-if="sortCol === 'conversion_rate'" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-3 py-2 text-right font-medium text-gray-700 cursor-pointer select-none" @click="toggleSort('suspects')">
                  Suspects <span v-if="sortCol === 'suspects'" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-3 py-2 text-center font-medium text-gray-700 cursor-pointer select-none" @click="toggleSort('is_active')">
                  Active <span v-if="sortCol === 'is_active'" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-3 py-2 text-center font-medium text-gray-700 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="rule in sortedRules"
                :key="rule.id"
                class="border-t border-gray-100 hover:bg-gray-50"
              >
                <td class="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    :value="rule.id"
                    v-model="selectedRuleIds"
                    class="rounded border-gray-300"
                  />
                </td>
                <td class="px-3 py-2">
                  <div class="font-medium text-gray-900">{{ rule.name }}</div>
                  <div v-if="rule.suspect_hcc" class="text-[10px] text-gray-400">
                    HCC {{ rule.suspect_hcc }}
                  </div>
                </td>
                <td class="px-3 py-2">
                  <span class="font-mono">{{ rule.icd_codes_preview || '—' }}</span>
                  <span v-if="rule.icd_code_count > 3" class="text-gray-400 ml-0.5">
                    (+{{ rule.icd_code_count - 3 }})
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span
                    :class="confidenceBadgeClass(rule.confidence_tier)"
                    class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  >
                    {{ rule.confidence_tier_display }}
                  </span>
                </td>
                <td class="px-3 py-2 text-gray-600">
                  {{ rule.evidence_type_display }}
                </td>
                <td class="px-3 py-2 text-right text-gray-600">
                  <span v-if="rule.conversion_rate" class="font-medium">{{ (rule.conversion_rate * 100).toFixed(1) }}%</span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-3 py-2 text-right">
                  <span v-if="suspectCounts[rule.name] != null" class="font-medium">{{ suspectCounts[rule.name].toLocaleString() }}</span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-3 py-2 text-center">
                  <button
                    @click="toggleActive(rule)"
                    :class="rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'"
                    class="px-1.5 py-0.5 rounded text-[10px] font-medium hover:opacity-80"
                  >
                    {{ rule.is_active ? 'Active' : 'Inactive' }}
                  </button>
                </td>
                <td class="px-3 py-2 text-center">
                  <div class="flex items-center justify-center gap-2">
                    <button
                      @click="editRule(rule)"
                      class="text-gray-400 hover:text-blue-600"
                      title="Edit"
                    >
                      <FontAwesomeIcon :icon="['fas', 'pen-to-square']" />
                    </button>
                    <button
                      @click="testRule(rule)"
                      class="text-gray-400 hover:text-green-600"
                      title="Test"
                    >
                      <FontAwesomeIcon :icon="['fas', 'flask']" />
                    </button>
                    <button
                      @click="confirmDelete(rule)"
                      class="text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <FontAwesomeIcon :icon="['fas', 'trash']" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

    </div>

    <!-- Editor View -->
    <SuspectRuleEditor
      v-if="viewMode === 'edit'"
      class="p-4"
      :rule="editingRule"
      :attributes="attributes"
      :is-new="isNewRule"
      @save="onSave"
      @cancel="cancelEdit"
      @test="onTestFromEditor"
    />

    <!-- Test Modal -->
    <div
      v-if="showTestModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="showTestModal = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold">Test Rule: {{ testingRule?.name }}</h3>
        </div>
        <div class="px-6 py-4">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Performance Year</label>
            <select
              v-model="testYear"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
            >
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>

          <button
            @click="runTest"
            :disabled="testingInProgress"
            class="btn-primary w-full"
          >
            {{ testingInProgress ? 'Testing...' : 'Run Test' }}
          </button>

          <!-- Test Results -->
          <div v-if="testResult" class="mt-4 p-4 bg-gray-50 rounded border">
            <div class="text-lg font-semibold text-gray-800 mb-2">
              {{ testResult.suspect_count.toLocaleString() }} suspects found
            </div>
            <div v-if="testResult.suggested_icd_codes?.length" class="text-sm text-gray-600 mb-2">
              Suggested ICD codes: {{ testResult.suggested_icd_codes.map(c => c.code).join(', ') }}
            </div>
            <div v-if="testResult.sample_mbis?.length > 0" class="mt-3">
              <div class="text-xs text-gray-500 mb-1">Sample MBIs (first 10):</div>
              <div class="sensitive font-mono text-xs bg-white p-2 rounded border max-h-32 overflow-y-auto">
                <div v-for="mbi in testResult.sample_mbis" :key="mbi">{{ mbi }}</div>
              </div>
            </div>
          </div>

          <div v-if="testError" class="mt-4 p-4 bg-red-50 rounded border border-red-200 text-red-700 text-sm">
            {{ testError }}
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            @click="showTestModal = false"
            class="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="showDeleteModal = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-red-600">Delete Rule</h3>
        </div>
        <div class="px-6 py-4">
          <p class="text-gray-700">
            Are you sure you want to delete "<strong>{{ deletingRule?.name }}</strong>"?
            This action cannot be undone.
          </p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            @click="showDeleteModal = false"
            class="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            @click="deleteRule"
            :disabled="deleting"
            class="btn-danger"
          >
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SuspectRuleEditor from './SuspectRuleEditor.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'SuspectRules_RuleManager',
  components: {
    FontAwesomeIcon,
    SuspectRuleEditor,
    PageHeader,
    LoadingIndicator,
  },
  props: {
    attributes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // View state
      viewMode: 'list', // 'list' | 'edit'

      // Rules data
      rules: [],
      loading: false,

      // Selection
      selectedRuleIds: [],

      // Suspect counts per rule name
      suspectCounts: {},

      // Sort state
      sortCol: 'name',
      sortDir: 'asc',

      // Run state
      runningRules: false,
      runResult: null,

      // Filters
      filterConfidenceTier: '',
      filterActive: '',
      filterSearch: '',

      // Editor state
      editingRule: null,
      isNewRule: false,

      // Test modal
      showTestModal: false,
      testingRule: null,
      testYear: new Date().getFullYear(),
      testingInProgress: false,
      testResult: null,
      testError: null,

      // Delete modal
      showDeleteModal: false,
      deletingRule: null,
      deleting: false,
    };
  },
  computed: {
    yearOptions() {
      const current = new Date().getFullYear();
      return [current, current - 1, current - 2, current - 3];
    },
    filteredRules() {
      let result = this.rules;

      if (this.filterConfidenceTier) {
        result = result.filter(r => r.confidence_tier === this.filterConfidenceTier);
      }

      if (this.filterActive !== '') {
        const isActive = this.filterActive === 'true';
        result = result.filter(r => r.is_active === isActive);
      }

      if (this.filterSearch) {
        const search = this.filterSearch.toLowerCase();
        result = result.filter(r =>
          r.name.toLowerCase().includes(search) ||
          String(r.suspect_hcc).includes(search) ||
          (r.suspect_hcc_label || '').toLowerCase().includes(search)
        );
      }

      return result;
    },
    sortedRules() {
      const col = this.sortCol;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return [...this.filteredRules].sort((a, b) => {
        let va, vb;
        if (col === 'suspects') {
          va = this.suspectCounts[a.name] ?? -1;
          vb = this.suspectCounts[b.name] ?? -1;
        } else if (col === 'conversion_rate') {
          va = a.conversion_rate ?? -1;
          vb = b.conversion_rate ?? -1;
        } else if (col === 'is_active') {
          va = a.is_active ? 1 : 0;
          vb = b.is_active ? 1 : 0;
        } else {
          va = (a[col] || '').toString().toLowerCase();
          vb = (b[col] || '').toString().toLowerCase();
        }
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    },
    allFilteredSelected() {
      if (this.filteredRules.length === 0) return false;
      return this.filteredRules.every(r => this.selectedRuleIds.includes(r.id));
    },
  },
  methods: {
    toggleSort(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortCol = col;
        this.sortDir = 'asc';
      }
    },

    async fetchRules() {
      this.loading = true;
      try {
        const response = await axios.get('api/coding/suspect-rules/');
        this.rules = response.data;
        // Auto-select all active rules
        this.selectedRuleIds = this.rules.filter(r => r.is_active).map(r => r.id);
        // Fetch suspect counts per rule
        this.fetchSuspectCounts();
      } catch (error) {
        console.error('Error fetching suspect rules:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchSuspectCounts() {
      try {
        const response = await axios.get('api/coding/suspect-rules/current-suspects/', {
          params: { startRow: 0, endRow: 0 },
        });
        const countMap = {};
        (response.data.summary || []).forEach(s => {
          countMap[s.rule_name] = s.suspect_count;
        });
        this.suspectCounts = countMap;
      } catch (error) {
        console.error('Error fetching suspect counts:', error);
      }
    },

    toggleSelectAll() {
      if (this.allFilteredSelected) {
        // Deselect all filtered rules
        const filteredIds = new Set(this.filteredRules.map(r => r.id));
        this.selectedRuleIds = this.selectedRuleIds.filter(id => !filteredIds.has(id));
      } else {
        // Select all filtered rules
        const currentSet = new Set(this.selectedRuleIds);
        this.filteredRules.forEach(r => currentSet.add(r.id));
        this.selectedRuleIds = [...currentSet];
      }
    },

    async runSelectedRules() {
      if (this.selectedRuleIds.length === 0) return;

      this.runningRules = true;
      this.runResult = null;

      try {
        const response = await axios.post('api/coding/suspect-rules/run/', {
          rule_ids: this.selectedRuleIds,
        });
        this.runResult = response.data;
        this.fetchSuspectCounts();
      } catch (error) {
        console.error('Error running suspect rules:', error);
        this.runResult = {
          success: false,
          error: error.response?.data?.error || error.message,
        };
      } finally {
        this.runningRules = false;
      }
    },

    createNew() {
      this.editingRule = {
        name: '',
        description: '',
        condition: { logic: 'AND', conditions: [] },
        suggested_icd_codes: [],
        suspect_hcc: null,
        suspect_hcc_label: '',
        evidence_type: 'historical',
        evidence_citation: '',
        evidence_source: '',
        confidence_tier: 'medium',
        is_active: true,
      };
      this.isNewRule = true;
      this.viewMode = 'edit';
    },

    editRule(rule) {
      axios.get(`api/coding/suspect-rules/${rule.id}/`)
        .then(response => {
          this.editingRule = response.data;
          this.isNewRule = false;
          this.viewMode = 'edit';
        })
        .catch(error => {
          console.error('Error fetching rule details:', error);
          alert('Error loading rule details');
        });
    },

    cancelEdit() {
      this.viewMode = 'list';
      this.editingRule = null;
      this.isNewRule = false;
    },

    async onSave(ruleData) {
      try {
        if (this.isNewRule) {
          await axios.post('api/coding/suspect-rules/', ruleData);
        } else {
          await axios.put(`api/coding/suspect-rules/${this.editingRule.id}/`, ruleData);
        }
        this.viewMode = 'list';
        this.editingRule = null;
        this.isNewRule = false;
        await this.fetchRules();
      } catch (error) {
        console.error('Error saving rule:', error);
        const msg = error.response?.data?.detail ||
                    JSON.stringify(error.response?.data) ||
                    error.message;
        alert('Error saving rule: ' + msg);
      }
    },

    async toggleActive(rule) {
      try {
        const response = await axios.post(`api/coding/suspect-rules/${rule.id}/toggle-active/`);
        rule.is_active = response.data.is_active;
      } catch (error) {
        console.error('Error toggling active status:', error);
      }
    },

    testRule(rule) {
      this.testingRule = rule;
      this.testResult = null;
      this.testError = null;
      this.showTestModal = true;
    },

    onTestFromEditor(rule) {
      this.testingRule = rule;
      this.testResult = null;
      this.testError = null;
      this.showTestModal = true;
    },

    async runTest() {
      if (!this.testingRule) return;

      this.testingInProgress = true;
      this.testResult = null;
      this.testError = null;

      try {
        const response = await axios.post(
          `api/coding/suspect-rules/${this.testingRule.id}/test/`,
          { year: this.testYear }
        );
        this.testResult = response.data;
      } catch (error) {
        console.error('Error testing rule:', error);
        this.testError = error.response?.data?.error || error.message;
      } finally {
        this.testingInProgress = false;
      }
    },

    confirmDelete(rule) {
      this.deletingRule = rule;
      this.showDeleteModal = true;
    },

    async deleteRule() {
      if (!this.deletingRule) return;

      this.deleting = true;
      try {
        await axios.delete(`api/coding/suspect-rules/${this.deletingRule.id}/`);
        this.showDeleteModal = false;
        this.deletingRule = null;
        await this.fetchRules();
      } catch (error) {
        console.error('Error deleting rule:', error);
        alert('Error deleting rule: ' + (error.response?.data?.detail || error.message));
      } finally {
        this.deleting = false;
      }
    },

    confidenceBadgeClass(tier) {
      switch (tier) {
        case 'very_high':
          return 'bg-green-100 text-green-800';
        case 'high':
          return 'bg-blue-100 text-blue-800';
        case 'medium':
          return 'bg-yellow-100 text-yellow-800';
        case 'low':
          return 'bg-gray-100 text-gray-600';
        default:
          return 'bg-gray-100 text-gray-600';
      }
    },
  },
  mounted() {
    this.fetchRules();
  },
};
</script>
